// ink-component
import React, { ReactNode } from 'react';
import styled, { ThemeProvider } from 'styled-components';

import { emptyStateTokens } from '../../tokens/components/EmptyState/tokens';
import getGlobalProps from '../../helpers/constants/global-props';

import IconWithText from '../IconWithText/IconWithText';

export interface EmptyStateProps {
  /** Defines if block or page empty state */
  type: 'block' | 'page' | 'mini';
  /** Text to tell user why the empty state */
  text: ReactNode;
  /** Ink Button or Ink Dropdown with possible actions (highly recommended) */
  children?: ReactNode;
  /** Defines the illustration in the empty state. Defaults to document. */
  icon?:
    | 'trophy'
    | 'checkmark'
    | 'setup'
    | 'document'
    | 'network'
    | 'pencil'
    | 'notfound'
    | 'nopermission'
    | 'error'
    | 'accept'
    | '';
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
}

const handleType = type => {
  switch (type) {
    case 'block':
      return `calc(${emptyStateTokens.CorePaddingDefault} / 2)`;
    case 'mini':
      return `calc(${emptyStateTokens.CorePaddingDefault} / 8)`;
    default:
      return emptyStateTokens.CorePaddingDefault;
  }
};

const EmptyStateWrapper = styled.div`
  text-align: center;
  padding: ${({ theme }) => handleType(theme.type)};
`;

const globalProps = getGlobalProps({});

/**
 * @purpose Indicates absence of any data to display and guides user to take action
 * @categories layout
 * @aka empty house, placeholder, CTA
 */
function EmptyState(props: EmptyStateProps) {
  const { type, icon, text, children } = props;

  return (
    <ThemeProvider theme={currentTheme => ({ ...currentTheme, type })}>
      <EmptyStateWrapper data-testid={props['data-testid']} {...globalProps}>
        <IconWithText size={type} icon={icon} text={text} aria-hidden="true" />
        {children && children}
      </EmptyStateWrapper>
    </ThemeProvider>
  );
}

EmptyState.defaultProps = {
  icon: 'document',
  children: undefined,
  'data-testid': undefined,
};

EmptyState.displayName = 'EmptyState';

export default EmptyState;
